import React from "react";
import clsx from "clsx/lite";
import { useGlobalContext } from "context/global";
import Money from "components/money";
import useCartSummary from "hooks/useCartSummary";
import { ReactComponent as Checkmark } from "assets/svgs/checkmark.svg";
import { areFrequenciesEqual } from "utils/frequency";

export const FrequencyOption = ({ bundle, title, isActive, handleClick, benefits, allowOneTimePurchase = false }) => {
	const { cartSettings: { subscription, boxes } } = useGlobalContext();
	const totalWeight = bundle.product.variant.weight;

	const frequencyData = subscription.frequencies.find(f => areFrequenciesEqual(f, bundle.frequency));
	const currentBox = boxes.find(box => box.size.value === totalWeight);
	const { pricePerMeal, highestBoxPrice } = useCartSummary({
		currentBox, products: [{ ...bundle.product, quantity: 1 }],
		totalWeight,
		calcFirstOrderDiscount: true
	});

	const compareAtPrice = highestBoxPrice !== pricePerMeal ? highestBoxPrice : null;

	const label = benefits.length > 0 && subscription.discountPercentage ? `-${subscription.discountPercentage}%` : null;

	return (
		<BundleOption
			isActive={isActive}
			isLabelAsBadge
			isTitleBold={allowOneTimePurchase}
			data={{
				...frequencyData,
				title,
				benefits,
				label,
				description: <Description
					hideMealsInMobile={allowOneTimePurchase}
					totalMeals={totalWeight}
					compareAtPrice={compareAtPrice}
					pricePerMeal={pricePerMeal} />
			}}
			onClick={handleClick}
		/>
	);
};

export const OneTimePurchaseOption = ({ bundle, isActive, highestBoxPrice, benefits, handleClick }) => {
	const { translation, cartSettings } = useGlobalContext();
	const totalWeight = bundle.product.variant.weight;
	const currentBox = cartSettings.boxes.find(box => box.size.value === totalWeight);

	const compareAtPrice = highestBoxPrice !== currentBox ? highestBoxPrice : null;

	return (
		<BundleOption
			isActive={isActive}
			isTitleBold
			data={{
				name: translation.cart?.one_time_purchase || "One time purchase",
				benefits,
				description:
					<Description
						totalMeals={totalWeight}
						hideMealsInMobile
						pricePerMeal={currentBox.price}
						compareAtPrice={compareAtPrice}
					/>
			}}
			onClick={handleClick}
		/>
	);
};


const BundleOption = ({ data, onClick, isActive, isLabelAsBadge = false, isTitleBold = false }) => {
	const Label = ({ className }) => {
		if (!data.label) return null;

		if (isLabelAsBadge)
			return (
				<span className={clsx("bg-orange-discount rounded-3xl mt-px px-4 py-1 text-small text-white", className)}>
					{data.label}
				</span>
			);

		return <p className={clsx("text-orange-discount font-boldParagraph text-small", className)}>{data.label}</p>;

	};

	const showBenefits = data.benefits?.length > 0 && isActive;
	const showMealsOnly = data._type === "mealsPerWeek";

	return (
		<div className={clsx(showMealsOnly && "w-full")}>
			<button
				onClick={onClick}
				className={clsx(
					"flex items-center py-4 px-5 w-full border-beige border-2 rounded-xl max-h-16", isActive && "border-black",
					showBenefits && "cursor-auto", showMealsOnly ? "justify-center" : "justify-between"
				)}
			>
				<p className={clsx("text-left", isTitleBold && "font-boldParagraph")}>{data.title || data.name || data.value}</p>
				<Label className={clsx(!isLabelAsBadge && "hidden lg:block")} />
				<div className="flex flex-col">
					{typeof data.description === "string" ?
						<p className="text-secondary text-small">{data.description}</p> : data.description}
					{!isLabelAsBadge && <Label className="lg:hidden w-fit mx-auto" />}
				</div>
			</button>
			{showBenefits && <div className={clsx("bg-[#FAF9F8] relative pt-3 pb-1 px-5 transition-all delay-100",
				"border-beige border-2 rounded-b-xl -mt-2 -z-10"
			)}>
				{data.benefits.map((benefit, index) => (
					<div key={index} className="my-3 flex flex-row gap-x-3 items-center">
						<Checkmark className=" !size-3 flex-shrink-0" role="presentation" aria-hidden="true" />
						<p className="text-secondary">{benefit}</p>
					</div>
				))}
			</div>}
		</div>
	);
};

const Description = ({ hideMealsInMobile = false, totalMeals, pricePerMeal, compareAtPrice }) => {
	const { translation } = useGlobalContext();
	return (
		<p className="text-small flex items-center gap-x-1">
			<span className={clsx(hideMealsInMobile && "hidden lg:block")}>
				{totalMeals} {translation.cart.meals} {translation.other.for?.toLowerCase()}{" "}
			</span>
			{compareAtPrice && (
				<span className="line-through text-secondary text-small"
					aria-label={`Original price: ${compareAtPrice}`}>
					<Money value={compareAtPrice} />{" "}
				</span>
			)}
			<strong className="text-black"><Money value={pricePerMeal} /> / {translation.cart.meal}</strong>
		</p>
	);
};

export default BundleOption;